import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'
import { useAuth } from '@/context/AuthContext'
import { AuthAlert } from '@/components/auth/AuthAlert'

export function VerifyEmail() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const token = searchParams.get('token')

  const [loading, setLoading] = useState(true)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing verification link.')
      setLoading(false)
      return
    }

    api
      .post('/auth/verify-email', { token })
      .then(() => {
        setSuccess(true)
        setLoading(false)
      })
      .catch((err: any) => {
        setError(err.response?.data?.detail || 'Verification failed. The link may have expired.')
        setLoading(false)
      })
  }, [token])

  if (loading) {
    return (
      <div className="space-y-6 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
        </div>
        <div className="space-y-1.5">
          <h1 className="text-2xl font-bold tracking-tight">Verifying your email...</h1>
          <p className="text-sm text-muted-foreground">Please wait while we confirm your email address.</p>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="space-y-6 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10">
          <CheckCircle2 className="h-7 w-7 text-emerald-500" />
        </div>
        <div className="space-y-1.5">
          <h1 className="text-2xl font-bold tracking-tight">Email Verified!</h1>
          <p className="text-sm text-muted-foreground">Your email address has been verified successfully.</p>
        </div>
        <Button className="w-full" onClick={() => navigate(isAuthenticated ? '/' : '/login')}>
          {isAuthenticated ? 'Go to Dashboard' : 'Sign in'}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/10">
        <AlertCircle className="h-7 w-7 text-amber-500" />
      </div>
      <div className="space-y-1.5">
        <h1 className="text-2xl font-bold tracking-tight">Verification Failed</h1>
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
      {error && <AuthAlert message={error} />}
      <div className="flex flex-col gap-2">
        <Button className="w-full" variant="outline" onClick={() => navigate('/')}>
          Go to Dashboard
        </Button>
        <Button asChild variant="ghost" className="w-full">
          <Link to="/login">Sign in</Link>
        </Button>
      </div>
    </div>
  )
}
